import React, { useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Clock, Award, TrendingUp, ArrowRight, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { getUser, getEnrolledCourses, COURSES, getNotifications } from '@/lib/data';
import axios from 'axios';
import { UserContext } from '@/lib/User';

export default function Dashboard() {
  // const user = getUser() || { session: 'Student' };
   const {user} = useContext(UserContext);

 const getUserDetails = async () => {
  const data = new FormData();
  data.append("session", user?.session);
  console.log(user);
  const res = await axios.post(
    "https://xceededge.com/get-user.php",data, { headers: { "Content-Type": "multipart/form-data" } }
    
  );

  if (res?.data?.status === "success") {
    return res.data.user;
  }
};

  useEffect(() => {
    getUserDetails()
  }, [])



  const enrolled = getEnrolledCourses();
  const notifications = getNotifications().filter(n => !n.read);

  const enrichedCourses = enrolled.map(e => ({
    ...e,
    course: COURSES.find(c => c.id === e.courseId),
  })).filter(e => e.course);

  const continueCourse = enrichedCourses.find(c => c.progress > 0 && c.progress < 100) || enrichedCourses[0];

  const stats = [
    { label: 'Enrolled Courses', value: enrolled.length, icon: BookOpen },
    { label: 'Hours Learned', value: enrolled.length * 12, icon: Clock },
    { label: 'Certificates', value: enrolled.filter(e => e.progress === 100).length, icon: Award },
    {
      label: 'Avg Progress',
      value: enrolled.length ? Math.round(enrolled.reduce((s, e) => s + e.progress, 0) / enrolled.length) + '%' : '0%',
      icon: TrendingUp
    },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      {/* Welcome */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-3xl border border-white/80 p-7 shadow-sm relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #FED202 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
          Welcome back, <span className="text-primary">{user?.name}</span>! 👋
        </h1>
        <p className="text-muted-foreground mt-2 font-body text-base">Ready to continue your learning journey?</p>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            whileHover={{ y: -3, transition: { duration: 0.2 } }}
            className="glass rounded-2xl border border-white/80 p-5 shadow-sm relative overflow-hidden group"
          >
            <motion.div
              className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            />
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center mb-3">
              <stat.icon className="w-5 h-5 text-primary" />
            </div>
            <p className="font-heading text-2xl font-bold tracking-tight">{stat.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5 font-body">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Continue Learning */}
      {continueCourse && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass rounded-2xl border border-white/80 p-6 shadow-sm"
        >
          <h2 className="font-heading font-bold text-lg mb-4 tracking-tight">Continue Learning</h2>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              className="w-16 h-16 rounded-2xl bg-primary/15 flex items-center justify-center flex-shrink-0"
            >
              <PlayCircle className="w-8 h-8 text-primary" />
            </motion.div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold truncate">{continueCourse.course.title}</h3>
              <div className="flex items-center gap-3 mt-2.5">
                <Progress value={continueCourse.progress} className="flex-1 h-1.5 rounded-full" />
                <span className="text-sm font-semibold text-foreground/70 shrink-0">{continueCourse.progress}%</span>
              </div>
            </div>
            <Link to={`/player/${continueCourse.courseId}`}>
              <Button className="gap-2 rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/20 font-semibold shrink-0">
                Resume <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </motion.div>
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass rounded-2xl border border-white/80 p-6 shadow-sm"
        >
          <h2 className="font-heading font-bold text-lg mb-4 tracking-tight">Notifications</h2>
          <div className="space-y-3">
            {notifications.map((n, i) => (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white/60 rounded-xl border border-white/80"
              >
                <motion.div
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                  className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0"
                />
                <div>
                  <p className="text-sm font-semibold font-heading">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 font-body">{n.message}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recommended */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="glass rounded-2xl border border-white/80 p-6 shadow-sm"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-bold text-lg tracking-tight">Recommended for You</h2>
          <Link to="/courses">
            <Button variant="ghost" size="sm" className="gap-1 text-primary font-medium rounded-xl hover:bg-primary/10">
              View All <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {COURSES.filter(c => !enrolled.find(e => e.courseId === c.id)).slice(0, 3).map((course, i) => (
            <Link key={course.id} to={`/course/${course.id}`} className="group">
              <motion.div
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                className="bg-white/60 hover:bg-white/90 border border-white/80 rounded-xl p-4 transition-colors shadow-sm"
              >
                <h3 className="font-heading font-semibold text-sm group-hover:text-primary transition-colors line-clamp-1">{course.title}</h3>
                <p className="text-xs text-muted-foreground mt-1 font-body">{course.level} · {course.duration}</p>
                <p className="text-sm font-heading font-bold text-primary mt-2">₹{course.price.toLocaleString('en-IN')}</p>
              </motion.div>
            </Link>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}